import cronParser from 'cron-parser'
import dayjs from 'dayjs'
import { first, forEach, get, join, keyBy, map, mapValues, split } from 'lodash'
import moment, { Moment } from 'moment'

import { TCron } from './types'

export function getCronTypes(): Record<string, string> {
  return {
    once: i18n.t(`once`, `Einmalig`),
    daily: i18n.t(`daily`, `Täglich`),
    someDays: i18n.t(`some_weekdays`, `An einigen Wochentagen`),
    firstOfMonth: i18n.t(`first_of_month`, `Erster des Monats`),
    lastOfMonth: i18n.t(`last_of_month`, `Letzter des Monats`),
    monthly: i18n.t(`monthly`, `Monatlich`),
  }
}

export function getCronDays(): { label: string; value: number }[] {
  return [
    {
      label: i18n.t(`monday`, `Montag`),
      value: 1,
    },
    {
      label: i18n.t(`tuesday`, `Dienstag`),
      value: 2,
    },
    {
      label: i18n.t(`wednesday`, `Mittwoch`),
      value: 3,
    },
    {
      label: i18n.t(`thursday`, `Donnerstag`),
      value: 4,
    },
    {
      label: i18n.t(`friday`, `Freitag`),
      value: 5,
    },
    {
      label: i18n.t(`saturday`, `Samstag`),
      value: 6,
    },
    {
      label: i18n.t(`sunday`, `Sonntag`),
      value: 7,
    },
  ]
}

const getCronDaysByValue = (): any =>
  mapValues(keyBy(getCronDays(), 'value'), 'label')

export const generateCronPattern = (cronConfig: TCron): string => {
  const { cronType, cronTimes, firstRunTime, cronDays } = cronConfig

  if (cronType === 'immediately' || cronType === 'once') {
    return '0 0 * * *'
  }

  if (cronType !== 'custom' && firstRunTime) {
    let pattern

    switch (cronType) {
      case 'daily':
        pattern = '* * *'
        break
      case 'someDays': {
        const days = cronDays || []
        pattern =
          days.length > 0
            ? `* * ${join(days, ',')}`
            : i18n.t(`choose_weedays`, `Bitte Wochentage auswählen`)
        break
      }

      case 'firstOfMonth':
        pattern = '1 * *'
        break
      case 'lastOfMonth':
        pattern = 'L * *'
        break
      case 'monthly':
        pattern = `${moment(firstRunTime).date()} * *`
        break
    }
    const time = (first(cronTimes) || '0:00').split(':')

    return `${Number(time[1])} ${Number(time[0])} ${pattern}`
  }
  return ''
}

export const generateCronPatterns = (cronConfig: TCron): string[] => {
  return map(cronConfig.cronTimes, (time) => {
    return generateCronPattern({ ...cronConfig, cronTimes: [time] })
  })
}

function getCronType(cronPattern: string): string {
  const parts = split(cronPattern, ' ')
  const minute = parts[0]
  const hour = parts[1]
  const dayOfMonth = parts[2]
  const month = parts[3]
  const dayOfWeek = parts[4]

  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'daily'
  }

  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    return 'someDays'
  }

  if (dayOfMonth === '1' && month === '*' && dayOfWeek === '*') {
    return 'firstOfMonth'
  }

  if (dayOfMonth === 'L' && month === '*' && dayOfWeek === '*') {
    return 'lastOfMonth'
  }

  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    return 'monthly'
  }

  return 'custom'
}

export const getCronConfig = (cronPatterns: string[]): TCron => {
  let cronConfig: TCron | undefined = undefined
  const cronTimes = [] as string[]
  forEach(cronPatterns, (cronPattern) => {
    const parts = split(cronPattern, ' ')
    const minute = parts[0]
    const hour = parts[1]
    const dayOfMonth = parts[2]
    const month = parts[3]
    const dayOfWeek = parts[4]

    if (!cronConfig) {
      cronConfig = {
        cronType: getCronType(cronPattern),
        firstRunTime: moment().toISOString(),
        cronDays: dayOfWeek === '*' ? [] : split(dayOfWeek, ','),
        cronTimes: [],
      }
    }
    cronTimes.push(`${hour}:${minute}`)
  })
  // console.log('getCronConfig', { cronPatterns, cronConfig, cronTimes })
  return {
    ...(cronConfig as unknown as TCron),
    cronTimes,
  }
}

export const getTextFromPattern = (pattern: string): string => {
  const type = getCronType(pattern)
  const parts = split(pattern, ' ')
  const minute = parts[0]
  const hour = parts[1]
  const dayOfMonth = parts[2]
  const month = parts[3]
  const dayOfWeek = parts[4]

  const result: string[] = []

  if (type !== 'someDays') {
    result.push(get(getCronTypes(), type))
  }

  if (type === 'someDays') {
    result.push(
      join(
        map(
          split(dayOfWeek, ','),
          (day) => `${get(getCronDaysByValue(), day)}s`,
        ),
        ', ',
      ),
    )
  }

  if (type === 'monthly') {
    result.push(i18n.t(`monthly_on`, `am`))
    result.push(`${dayOfMonth}.`)
  }

  result.push(i18n.t('time_at', 'um'))
  result.push(moment(`${hour}:${minute}`, 'HH:mm').format('HH:mm'))

  return result.join(' ')
}

export const getNextRun = (
  pattern: string,
  currentDate: string | Moment = moment().toISOString() as string,
  count = 1,
  cronTimes?: string[],
  lastDate?: string | null,
): string[] | null => {
  try {
    const items: string[] = []
    let usedPattern = pattern
    const patternParts = split(pattern, ' ')
    const dayOfMonth = patternParts[2]
    const isLastOfMonth = dayOfMonth === 'L'
    if (isLastOfMonth) {
      patternParts[2] = '1'
      usedPattern = join(patternParts, ' ')
    }
    const interval = cronParser.parseExpression(usedPattern, {
      currentDate: moment(currentDate).isValid()
        ? (currentDate as string)
        : moment().toISOString(),
    })
    for (let i = 1; i <= count; i++) {
      const originalTime = moment(interval.next().toString())
      const time = moment(originalTime)
      if (isLastOfMonth) {
        time.subtract(1, 'month')
        time.endOf('month')
        time.set({
          hour: originalTime.get('hour'),
          minute: originalTime.get('minute'),
        })
      }
      if (cronTimes) {
        cronTimes.forEach((cronTime) => {
          const split: any[] = cronTime.split(':')
          time.set({
            hour: split[0],
            minute: split[1],
          })
          items.push(time.format('DD.MM.YYYY HH:mm'))
        })
      } else {
        items.push(time.format('DD.MM.YYYY HH:mm'))
      }
    }
    return items.slice(0, 5).filter((item) => {
      return (
        !lastDate ||
        moment(item, 'DD.MM.YYYY HH:mm').isSameOrBefore(moment(lastDate))
      )
    })
  } catch (error) {
    return null
  }
}
export const validateCronPattern =
  (config: TCron) =>
  // eslint-disable-next-line react/display-name
  (pattern: string): string | null => {
    const { firstRunTime, cronType } = config

    if (cronType === 'immediately' || cronType === 'once') return null

    const start = moment(firstRunTime)

    return getNextRun(pattern, start)
      ? null
      : i18n.t(`invalid_conf`, `Ungültige Konfiguration`)
  }

export const getStringFromCron = (cron: string, config: TCron): string => {
  const { cronType, cronTimes, firstRunTime } = config

  if (cronType === 'once') {
    return i18n.t('once_on', {
      defaultValue: `Einmalig am {{date}} um {{time}}`,
      date: dayjs(firstRunTime).format('DD.MM.YYYY'),
      time: cronTimes[0],
    })
  }

  const parts = cron.split(' ')
  const dayOfMonth = parts[2]
  const month = parts[3]
  const dayOfWeek = parts[4]

  const um =
    cronTimes.length > 1
      ? cronTimes.slice(0, -1).join(',') +
        ` ` +
        i18n.t('and', { defaultValue: 'und' }) +
        ` ` +
        cronTimes[cronTimes.length - 1]
      : cronTimes[0]

  // console.log(cronTimes, um)
  if (month === '*' && dayOfWeek === '*') {
    if (dayOfMonth === '*') {
      return i18n.t('daily_at_fixed', {
        defaultValue: 'Täglich um {{time}}',
        time: um,
      })
    }
    if (dayOfMonth === '1') {
      return i18n.t('first_of_month_at_fixed', {
        defaultValue: 'Am Ersten des Monats um {{time}}',
        time: um,
      })
    }
    if (dayOfMonth === 'L') {
      return i18n.t('last_of_month_at_fixed', {
        defaultValue: 'Am Letzten des Monats um {{time}}',
        time: um,
      })
    }
    return i18n.t('monthly_on_fixed', {
      defaultValue: 'Monatlich am {{day}}. um {{time}}',
      day: dayOfMonth,
      time: um,
    })
  }

  if (dayOfMonth === '*' && month === '*') {
    const days = dayOfWeek.split(',')
    const cronDaysResult = getCronDays().filter((cronDay) =>
      days.includes(String(cronDay.value)),
    )

    const resultDayLabels = cronDaysResult.map((cronDay) => cronDay.label)

    const parsedDays =
      resultDayLabels.length === 1
        ? resultDayLabels[0]
        : `${
            resultDayLabels.slice(0, -1).join(', ') +
            ` ${i18n.t('and_lower', 'und')} ` +
            resultDayLabels[resultDayLabels.length - 1]
          }`

    return i18n.t('specific_days_at_time_fixed', {
      defaultValue: '{{days}} um {{time}}',
      days: parsedDays,
      time: um,
    })
  }

  return ''
}
